// src/redux/adminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';

const API = process.env.REACT_APP_API_URL;
const hdr = (token) => ({
  'Content-Type': 'application/json',
  'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
  Authorization: token ? `Bearer ${token}` : undefined,
});
const gt = (thunkAPI) => thunkAPI.getState().auth.user?.token;

// ── Stats ──
export const fetchAdminStats = createAsyncThunk('admin/stats', async (_, t) => {
  const r = await fetch(`${API}/api/admin/stats`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Users ──
export const fetchAdminUsers = createAsyncThunk('admin/users', async (p = {}, t) => {
  const q = new URLSearchParams(p).toString();
  const r = await fetch(`${API}/api/admin/users?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchUserDetail = createAsyncThunk('admin/userDetail', async (id, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updateAdminUser = createAsyncThunk('admin/updateUser', async ({ id, updates }, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Moderation ──
export const banUser = createAsyncThunk('admin/ban', async ({ id, reason }, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}/ban`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ reason }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const unbanUser = createAsyncThunk('admin/unban', async (id, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}/unban`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const deleteUser = createAsyncThunk('admin/deleteUser', async (id, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const resetUserPassword = createAsyncThunk('admin/resetPw', async (id, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}/reset-password`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Super Admin ──
export const toggleAdminRole = createAsyncThunk('admin/toggleRole', async (id, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}/toggle-admin`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const changeUserPassword = createAsyncThunk('admin/changePw', async ({ id, newPassword }, t) => {
  const r = await fetch(`${API}/api/admin/users/${id}/change-password`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ newPassword }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Points ──
export const addPointsIndividual = createAsyncThunk('admin/ptsInd', async (body, t) => {
  const r = await fetch(`${API}/api/admin/points/individual`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const addPointsBulk = createAsyncThunk('admin/ptsBulk', async (body, t) => {
  const r = await fetch(`${API}/api/admin/points/bulk`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Notifications ──
export const adminSendNotification = createAsyncThunk('admin/sendNotif', async (body, t) => {
  const r = await fetch(`${API}/api/admin/notifications/send`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const adminBroadcast = createAsyncThunk('admin/broadcast', async (body, t) => {
  const r = await fetch(`${API}/api/admin/notifications/broadcast`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Quests ──
export const fetchAdminQuests = createAsyncThunk('admin/quests', async (_, t) => {
  const r = await fetch(`${API}/api/admin/quests`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const createQuest = createAsyncThunk('admin/createQuest', async (body, t) => {
  const r = await fetch(`${API}/api/admin/quests`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updateQuest = createAsyncThunk('admin/updateQuest', async ({ id, updates }, t) => {
  const r = await fetch(`${API}/api/admin/quests/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const deleteQuest = createAsyncThunk('admin/deleteQuest', async (id, t) => {
  const r = await fetch(`${API}/api/admin/quests/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

// ── Leaderboard ──
export const fetchAdminLeaderboard = createAsyncThunk('admin/leaderboard', async (_, t) => {
  const r = await fetch(`${API}/api/admin/leaderboard`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Audit Logs ──
export const fetchAuditLogs = createAsyncThunk('admin/logs', async (p = {}, t) => {
  const q = new URLSearchParams(p).toString();
  const r = await fetch(`${API}/api/admin/logs?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Economy ──
export const fetchEconomyStats = createAsyncThunk('admin/econStats', async (_, t) => {
  const r = await fetch(`${API}/api/admin/economy/stats`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchEconomyTransactions = createAsyncThunk('admin/econTxns', async (p = {}, t) => {
  const q = new URLSearchParams(p).toString();
  const r = await fetch(`${API}/api/admin/economy/transactions?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchEconomySponsorships = createAsyncThunk('admin/econSp', async (_, t) => {
  const r = await fetch(`${API}/api/admin/economy/sponsorships`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Transaction Actions ──
export const fetchTransactionDetail = createAsyncThunk('admin/txnDetail', async (id, t) => {
  const r = await fetch(`${API}/api/admin/economy/transactions/${id}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const reverseTransaction = createAsyncThunk('admin/txnReverse', async ({ id, reason }, t) => {
  const r = await fetch(`${API}/api/admin/economy/transactions/${id}/reverse`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ reason }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const flagTransaction = createAsyncThunk('admin/txnFlag', async ({ id, flag, reason }, t) => {
  const r = await fetch(`${API}/api/admin/economy/transactions/${id}/flag`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ flag, reason }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const addTransactionNote = createAsyncThunk('admin/txnNote', async ({ id, text }, t) => {
  const r = await fetch(`${API}/api/admin/economy/transactions/${id}/note`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ text }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const freezeTransaction = createAsyncThunk('admin/txnFreeze', async ({ id, freeze }, t) => {
  const r = await fetch(`${API}/api/admin/economy/transactions/${id}/freeze`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ freeze }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const manualTransfer = createAsyncThunk('admin/manualTransfer', async (body, t) => {
  const r = await fetch(`${API}/api/admin/economy/manual-transfer`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchEconomyAlerts = createAsyncThunk('admin/econAlerts', async (_, t) => {
  const r = await fetch(`${API}/api/admin/economy/alerts`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Partners ──
export const fetchAdminPartners = createAsyncThunk('admin/partners', async (p = {}, t) => {
  const clean = Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const q = new URLSearchParams(clean).toString();
  const r = await fetch(`${API}/api/admin/partners?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchPartnerDetail = createAsyncThunk('admin/partnerDetail', async (id, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const adminCreatePartner = createAsyncThunk('admin/createPartner', async (body, t) => {
  const r = await fetch(`${API}/api/admin/partners`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const adminUpdatePartner = createAsyncThunk('admin/updatePartner', async ({ id, updates }, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const adminValidatePartner = createAsyncThunk('admin/validatePartner', async ({ id, status, reason }, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}/validate`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ status, reason }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id, newStatus: status };
});

export const banPartner = createAsyncThunk('admin/banPartner', async ({ id, reason }, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}/ban`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ reason }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const unbanPartner = createAsyncThunk('admin/unbanPartner', async (id, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}/unban`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const deletePartner = createAsyncThunk('admin/deletePartner', async (id, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const resetPartnerPassword = createAsyncThunk('admin/resetPartnerPw', async (id, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}/reset-password`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const changePartnerPassword = createAsyncThunk('admin/changePartnerPw', async ({ id, newPassword }, t) => {
  const r = await fetch(`${API}/api/admin/partners/${id}/change-password`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ newPassword }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Slice ──
const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    stats: null, users: [], pagination: null, quests: [], leaderboard: [],
    logs: [], logsPagination: null, userDetail: null,
    economyStats: null, economyTxns: [], economyTxnsPagination: null, economySponsorships: [],
    txnDetail: null, economyAlerts: null,
    partners: [], partnerPagination: null, partnerSegmentCounts: {}, partnerStatusCounts: {},
    partnerDetail: null,
    status: 'idle', actionStatus: 'idle', actionMessage: null, error: null,
  },
  reducers: {
    clearAdminAction(s) { s.actionStatus = 'idle'; s.actionMessage = null; s.error = null; },
    clearUserDetail(s) { s.userDetail = null; },
  },
  extraReducers: b => {
    b
    .addCase(fetchAdminStats.pending,   s => { s.status = 'loading'; })
    .addCase(fetchAdminStats.fulfilled, (s, a) => { s.status = 'succeeded'; s.stats = a.payload; })
    .addCase(fetchAdminStats.rejected,  (s, a) => { s.status = 'failed'; s.error = a.payload; })

    .addCase(fetchAdminUsers.pending,   s => { s.status = 'loading'; })
    .addCase(fetchAdminUsers.fulfilled, (s, a) => { s.status = 'succeeded'; s.users = a.payload.users; s.pagination = a.payload.pagination; })
    .addCase(fetchAdminUsers.rejected,  (s, a) => { s.status = 'failed'; s.error = a.payload; })

    .addCase(fetchUserDetail.fulfilled, (s, a) => { s.userDetail = a.payload; })

    .addCase(fetchAdminQuests.fulfilled, (s, a) => { s.quests = a.payload; })
    .addCase(fetchAdminLeaderboard.fulfilled, (s, a) => { s.leaderboard = a.payload; })

    .addCase(fetchAuditLogs.fulfilled, (s, a) => { s.logs = a.payload.logs; s.logsPagination = a.payload.pagination; })

    .addCase(updateAdminUser.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(updateAdminUser.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(banUser.fulfilled,     (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.users = s.users.map(u => u._id === a.payload.id ? { ...u, isBanned: true } : u); })
    .addCase(banUser.rejected,      (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(unbanUser.fulfilled,   (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.users = s.users.map(u => u._id === a.payload.id ? { ...u, isBanned: false } : u); })
    .addCase(unbanUser.rejected,    (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(deleteUser.fulfilled,  (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.users = s.users.filter(u => u._id !== a.payload.id); })
    .addCase(deleteUser.rejected,   (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(resetUserPassword.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(resetUserPassword.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(toggleAdminRole.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.users = s.users.map(u => u._id === a.payload.id ? { ...u, isAdmin: a.payload.isAdmin } : u); })
    .addCase(toggleAdminRole.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(changeUserPassword.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(changeUserPassword.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(addPointsIndividual.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(addPointsIndividual.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(addPointsBulk.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(addPointsBulk.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(adminSendNotification.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(adminSendNotification.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(adminBroadcast.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(adminBroadcast.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(createQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.quests.push(a.payload.quest); })
    .addCase(createQuest.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(updateQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(updateQuest.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(deleteQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.quests = s.quests.filter(q => q._id !== a.payload.id); })
    .addCase(deleteQuest.rejected,  (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    // Economy
    .addCase(fetchEconomyStats.fulfilled, (s, a) => { s.economyStats = a.payload; })
    .addCase(fetchEconomyTransactions.fulfilled, (s, a) => { s.economyTxns = a.payload.transactions; s.economyTxnsPagination = a.payload.pagination; })
    .addCase(fetchEconomySponsorships.fulfilled, (s, a) => { s.economySponsorships = a.payload; })

    // Transaction actions
    .addCase(fetchTransactionDetail.fulfilled, (s, a) => { s.txnDetail = a.payload; })
    .addCase(fetchEconomyAlerts.fulfilled, (s, a) => { s.economyAlerts = a.payload; })
    .addCase(reverseTransaction.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(reverseTransaction.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(flagTransaction.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(flagTransaction.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(addTransactionNote.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(freezeTransaction.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(freezeTransaction.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(manualTransfer.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(manualTransfer.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    // Partners
    .addCase(fetchAdminPartners.pending, s => { s.status = 'loading'; })
    .addCase(fetchAdminPartners.fulfilled, (s, a) => { s.status = 'succeeded'; s.partners = a.payload.partners; s.partnerPagination = a.payload.pagination; s.partnerSegmentCounts = a.payload.segmentCounts; s.partnerStatusCounts = a.payload.statusCounts; })
    .addCase(fetchAdminPartners.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload; })
    .addCase(fetchPartnerDetail.fulfilled, (s, a) => { s.partnerDetail = a.payload; })
    .addCase(adminCreatePartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(adminCreatePartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(adminUpdatePartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(adminUpdatePartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(adminValidatePartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.partners = s.partners.map(p => p._id === a.payload.id ? { ...p, validationStatus: a.payload.newStatus } : p); })
    .addCase(adminValidatePartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(banPartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.partners = s.partners.map(p => p._id === a.payload.id ? { ...p, isBanned: true } : p); })
    .addCase(banPartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(unbanPartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.partners = s.partners.map(p => p._id === a.payload.id ? { ...p, isBanned: false } : p); })
    .addCase(unbanPartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(deletePartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.partners = s.partners.filter(p => p._id !== a.payload.id); })
    .addCase(deletePartner.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(resetPartnerPassword.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(resetPartnerPassword.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(changePartnerPassword.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(changePartnerPassword.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; });
  },
});

export const { clearAdminAction, clearUserDetail } = adminSlice.actions;
export default adminSlice.reducer;
